/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Form, Input } from 'antd';

const Provision = ({ getFieldDecorator, type, index, disabled = false }) => {
  return (
    <Form.Item
      label={`${type.toUpperCase()} Aircard #${index + 1}`}
      style={{ marginBottom: '0', paddingBottom: '0' }}
    >
      <Form.Item
        label="IMSI"
        style={{ display: 'inline-block', padding: '0 0.5rem', width: 'calc(50% - 2rem)' }}
      >
        {getFieldDecorator(`aircards.${type}[${index + 1}].imsi`, {
          validateTrigger: false,
          rules: [
            { required: true, message: 'IMSI is required' },
            { pattern: /^[\d]+$/, message: 'IMSI can only contain numeric values' },
            { pattern: /^(?:31000).*/, message: 'IMSI must start with 31000' },
            { len: 15, message: 'IMSI must be 15 digits long' },
          ],
        })(<Input disabled={disabled} placeholder="IMSI" maxLength={15} autoComplete="off" />)}
      </Form.Item>
      <Form.Item
        label="ESN"
        style={{
          display: 'inline-block',
          marginLeft: '1.8rem',
          padding: '0 0.5rem',
          width: 'calc(50% - 1rem)',
        }}
      >
        {getFieldDecorator(`aircards.${type}[${index + 1}].esn`, {
          validateTrigger: false,
          rules: [
            { required: true, message: 'ESN is required' },
            { pattern: /^[0-9A-F]+$/, message: 'ESN can only contain hex values' },
            { len: 8, message: 'ESN must be 8 digits long' },
          ],
          getValueFromEvent: ({ target: { value } = {} }) => {
            return !!value?.toUpperCase ? value.toUpperCase().trim() : value;
          },
        })(<Input disabled={disabled} placeholder="ESN" maxLength={8} autoComplete="off" />)}
      </Form.Item>
    </Form.Item>
  );
};

const Replace = ({ disabled, getFieldDecorator }) => {
  return (
    <Form.Item style={{ marginBottom: '0', paddingBottom: '0' }}>
      <Form.Item label="IMSI">
        {getFieldDecorator('imsi', {
          validateTrigger: false,
          rules: [
            { required: true, message: 'IMSI is required' },
            { pattern: /^[\d]+$/, message: 'IMSI can only contain numeric values' },
            { pattern: /^(?:31000).*/, message: 'IMSI must start with 31000' },
            { len: 15, message: 'IMSI must be 15 digits long' },
          ],
        })(<Input disabled={disabled} placeholder="IMSI" maxLength={15} autoComplete="off" />)}
      </Form.Item>
      <Form.Item label="ESN">
        {getFieldDecorator('esn', {
          validateTrigger: false,
          rules: [
            { required: true, message: 'ESN is required' },
            { pattern: /^[0-9A-F]+$/, message: 'ESN can only contain hex values' },
            { len: 8, message: 'ESN must be 8 digits long' },
          ],
          getValueFromEvent: ({ target: { value } = {} }) => {
            return !!value?.toUpperCase ? value.toUpperCase().trim() : value;
          },
        })(<Input disabled={disabled} placeholder="ESN" maxLength={8} autoComplete="off" />)}
      </Form.Item>
    </Form.Item>
  );
};

// ==========================
// Aircard Validation Helpers
// ==========================

/**
 * Checks the given aircard provisioning form values for duplicate field values.
 * If errors are found, returns an object for use with the antd form.setFields
 * method, else null.
 *
 * @param {[]} aircards
 */
export const checkDuplicateAircardValues = aircards => {
  let errorFound = false;
  const aircardErrors = {};

  if (!!aircards) {
    Object.entries(aircards).forEach(([type, aircardArray]) => {
      aircardErrors[type] = [];
      // Only validate within each aircard type, not between types
      const valueTable = {};
      aircardArray.forEach((aircard, index) => {
        if (!!aircard) {
          aircardErrors[type][index] = {};
          Object.entries(aircard).forEach(([field, value]) => {
            const key = `${field}:${JSON.stringify(value)}`;
            if (!!valueTable[key]) {
              errorFound = true;
              aircardErrors[type][index][field] = {
                value,
                errors: [new Error(`No duplicates allowed for ${field.toUpperCase()} field`)],
              };
            }
            valueTable[key] = true;
          });
        }
      });
    });
  }
  return errorFound ? { aircards: aircardErrors } : null;
};

/**
 * Checks the given aircard replacement form values against the existing aircard(s)
 * for duplicate field values. If errors are found, returns an object for use with
 * the antd form.setFields method, else null.
 *
 * @param {[]} existingAircards
 * @param {{}} values
 */
export const checkExistingAircardValues = (existingAircards, values) => {
  let errorFound = false;
  const errors = {};
  existingAircards.forEach(aircard => {
    Object.entries(values).forEach(([key, value]) => {
      if (aircard?.[key] === value) {
        errorFound = true;
        errors[key] = {
          value,
          errors: [new Error(`${key.toUpperCase()} must not match existing aircard values`)],
        };
      }
    });
  });
  return errorFound ? errors : null;
};

const AircardForm = { Provision: {}, Replace: {} };
AircardForm.Provision.DMA = Provision;
AircardForm.Replace.DMA = Replace;
AircardForm.Provision.EVDO = Provision;
AircardForm.Replace.EVDO = Replace;

export default AircardForm;
